import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useState } from "react";

const nav = [
  { to: "/", label: "Home" },
  { to: "/glossary", label: "Glossary" },
  { to: "/recipes", label: "Recipes" },
  { to: "/faq", label: "FAQ" },
  { to: "/admin", label: "Kitchen Desk" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="App min-h-screen" data-testid="app-shell">
      <header
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{ background: "rgba(244, 241, 235, 0.85)", borderBottom: "1px solid rgba(26,26,26,0.15)" }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="flex flex-col leading-none">
              <span className="font-display italic text-2xl md:text-3xl">Bite <span style={{ color: "var(--tomato)" }}>&amp;</span> Banter</span>
              <span className="hidden sm:block font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mt-1">Figure it out · Cook it · Eat it or Leave it</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8 font-ui text-sm tracking-widest uppercase">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `hover-underline ${isActive ? "font-semibold" : "opacity-70 hover:opacity-100"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <button
            className="md:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-black/10 px-6 py-4 flex flex-col gap-4 font-ui uppercase tracking-widest text-sm">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="fade-in">{children}</main>

      <footer className="mt-24 border-t border-black/15" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-display italic text-3xl">Bite &amp; Banter</div>
            <p className="font-body text-sm opacity-70 mt-2 max-w-sm">
              A searchable food glossary and recipe companion for the curious cook. Definitions worth reading. Recipes worth cooking.
            </p>
          </div>
          <div className="font-ui text-sm uppercase tracking-widest flex flex-col gap-2">
            <div className="opacity-50">Wander</div>
            {nav.map((n) => <Link key={n.to} to={n.to} className="hover-underline w-fit">{n.label}</Link>)}
          </div>
          <div className="font-body text-sm opacity-70">
            <div className="font-ui uppercase tracking-widest opacity-50 mb-2">Colophon</div>
            <p className="italic">Set in Cormorant Garamond, Outfit and Lora. Made with a hot pan and a cold drink.</p>
          </div>
        </div>
        <div className="border-t border-black/10">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 font-body text-[12px] leading-relaxed opacity-80 text-center" data-testid="site-copyright">
            © 2026 Bite &amp; Banter. All rights reserved. No part of this website may be reproduced, distributed, or transmitted in any form without the prior written permission of the publisher.
          </div>
        </div>
      </footer>
    </div>
  );
}
